import React, { FormEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { updateAddPhonePromptedAt, updateUserDetails } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import * as Analytics from 'analytics/analytics'

interface PhoneNum {
  phone: string
  prefix: string
}

interface AddPhoneForm {
  phoneNum: PhoneNum
}

interface Props {
  error?: any
  regionCode?: string
  phone?: string
  dismissable?: boolean
  trackPromptImpression?: boolean
  hasDismissed?: boolean
  onDismissChange?: (dismissed: boolean) => void
}

const ADD_PHONE_OPTIMIZELY_EVENT = {
  id: '4586532951031808',
  key: 'add_phone_number',
} as const

function AccountAccessAddPhone(props: Props) {
  const {
    regionCode,
    phone,
    dismissable,
    trackPromptImpression = true,
    hasDismissed: hasDismissedProp,
    onDismissChange,
  } = props

  const intl = useIntl()
  const termsCheckboxRef = useRef<HTMLInputElement>(null)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const dispatch = useAppDispatch()
  const [internalHasDismissed, setInternalHasDismissed] = useState(false)
  const isDismissedControlled = typeof hasDismissedProp === 'boolean'
  const hasDismissed = isDismissedControlled ? Boolean(hasDismissedProp) : internalHasDismissed

  const setHasDismissed = useCallback((nextState: boolean) => {
    if (!isDismissedControlled) {
      setInternalHasDismissed(nextState)
    }
    onDismissChange?.(nextState)
  }, [isDismissedControlled, onDismissChange])

  const onAddPhone = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const joinData = formToObject<AddPhoneForm>(event.currentTarget)
    dispatch(updateUserDetails({
      phone: joinData.phoneNum.phone,
      phonePrefix: joinData.phoneNum.prefix,
    }))
  }, [dispatch])

  const onClose = useCallback(() => {
    if (hasDismissed) {
      return modalContext.resolve({ loggedIn: true })
    }

    setHasDismissed(true)
  }, [modalContext, hasDismissed, setHasDismissed])

  useEffect(() => {
    if (trackPromptImpression) {
      dispatch(updateAddPhonePromptedAt(new Date()))
    }
    Analytics.trackClientEvent({
      subject: 'add_phone_number',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [dispatch, trackPromptImpression])

  useEffect(() => {
    if (phone?.length) {
      const optimizelyEvent = {
        optimizelyEventId: ADD_PHONE_OPTIMIZELY_EVENT.id,
        optimizelyEventKey: ADD_PHONE_OPTIMIZELY_EVENT.key,
      }

      Analytics.trackClientEvent({
        subject: 'add_phone_number',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
        ...optimizelyEvent,
      })
      modalContext.resolve({ loggedIn: true })
    }
  }, [modalContext, phone])

  const areYouSureTitle = intl.formatMessage({
    id: 'VCyRccvh4C',
    defaultMessage: 'Are you sure?',
    description: 'Modal title when user tries to dismiss the add phone number prompt',
  })

  const secureAccountTitle = intl.formatMessage({
    id: 'j0LLjzZ/g1',
    defaultMessage: 'Secure your account with a mobile number',
    description: 'Modal title for adding phone number to account',
  })

  const subtitleText = intl.formatMessage({
    id: '06j1MVhGhM',
    defaultMessage: 'Link your mobile to your account for added security and a smoother, faster login experience.',
    description: 'Subtitle explaining benefits of adding a phone number',
  })

  const warningText = intl.formatMessage({
    id: '4zdgDkj0sm',
    defaultMessage: 'Adding your phone number is highly recommended to help keep your account safe',
    description: 'Warning message when user tries to skip adding phone number',
  })

  const phoneLabel = intl.formatMessage({
    id: 'yCg8xuK8EI',
    defaultMessage: 'Phone number',
    description: 'Label for phone number input in add phone prompt',
  })

  const secureMyAccountLabel = intl.formatMessage({
    id: 'gOcKbwOUPc',
    defaultMessage: 'Secure my account',
    description: 'Button text to add phone number after initial dismissal',
  })

  const addMobileLabel = intl.formatMessage({
    id: 'UuTr4xCfmW',
    defaultMessage: 'Add mobile number',
    description: 'Primary button text to add mobile number to account',
  })

  return (
    <>
      <ModalHeader
        title={hasDismissed ? areYouSureTitle : secureAccountTitle}
        subtitle={!hasDismissed ? subtitleText : undefined}
        dismissible={dismissable}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onAddPhone}>
            <Group direction="vertical" gap={12}>
              {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                {warningText}
              </MessageBanner>}
              <PhoneInput
                id="phoneNum"
                name="phoneNum"
                label={phoneLabel}
                prefixCountry={regionCode}
                required
              />
              <CheckboxInput
                ref={termsCheckboxRef}
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText modalType="promptAddPhone" />
              </CheckboxInput>
              <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
              >
                {hasDismissed ? secureMyAccountLabel : addMobileLabel}
              </TextButton>
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
    phone: state.auth.account.phone,
  }
}

export default connect(mapStateToProps)(AccountAccessAddPhone)
